import { useRef, useCallback } from 'react'
import type { Particle, DistanceConstraint, ChainConfig, Vec2 } from '@/types'
import { vec2 } from '@/utils/vector'
import { createParticle, verletIntegrate, applyGravity, clampVelocity } from '@/utils/physics'
import { createDistanceConstraint, solveDistanceConstraints, enforcePinConstraint } from '@/utils/constraints'

interface ChainPhysicsOptions {
    config: ChainConfig
    anchorPoint: Vec2
}

export function useChainPhysics({ config, anchorPoint }: ChainPhysicsOptions) {
    const particlesRef = useRef<Particle[]>([])
    const constraintsRef = useRef<DistanceConstraint[]>([])
    const initializedRef = useRef(false)
    const anchorRef = useRef(anchorPoint)

    // Initialize chain on first call
    if (!initializedRef.current) {
        const particles: Particle[] = []
        const constraints: DistanceConstraint[] = []

        for (let i = 0; i < config.beadCount; i++) {
            const isHandle = i === config.beadCount - 1
            const pos = vec2(
                anchorPoint.x,
                anchorPoint.y + i * config.segmentLength,
            )
            particles.push(
                createParticle(
                    pos,
                    isHandle ? 2.0 : 1.0,
                    isHandle ? config.handleRadius : config.beadRadius,
                    i === 0, // pin the top bead
                ),
            )
        }

        for (let i = 0; i < config.beadCount - 1; i++) {
            constraints.push(
                createDistanceConstraint(i, i + 1, config.segmentLength, 1.0),
            )
        }

        particlesRef.current = particles
        constraintsRef.current = constraints
        initializedRef.current = true
    }

    const step = useCallback(
        (dt: number) => {
            const particles = particlesRef.current
            const constraints = constraintsRef.current

            applyGravity(particles, config.gravity)
            verletIntegrate(particles, dt, config.damping)
            solveDistanceConstraints(
                particles,
                constraints,
                config.constraintIterations,
            )
            enforcePinConstraint(particles[0], anchorRef.current)
            clampVelocity(particles, 500)
        },
        [config],
    )

    const setParticlePosition = useCallback(
        (index: number, pos: Vec2) => {
            const p = particlesRef.current[index]
            if (p) {
                // Set both position and previousPosition to zero out
                // implicit velocity — prevents energy buildup while dragging
                p.previousPosition.x = pos.x
                p.previousPosition.y = pos.y
                p.position.x = pos.x
                p.position.y = pos.y
            }
        },
        [],
    )

    const applyImpulse = useCallback(
        (index: number, impulse: Vec2) => {
            const p = particlesRef.current[index]
            if (p && !p.pinned) {
                // In verlet, impulse = moving previousPosition opposite to desired velocity
                p.previousPosition.x = p.position.x - impulse.x
                p.previousPosition.y = p.position.y - impulse.y
            }
        },
        [],
    )

    const getRestYPosition = useCallback(() => {
        return anchorRef.current.y + (config.beadCount - 1) * config.segmentLength
    }, [config])

    const updateAnchor = useCallback((newAnchor: Vec2) => {
        anchorRef.current = newAnchor
    }, [])

    return {
        particlesRef,
        constraintsRef,
        step,
        setParticlePosition,
        applyImpulse,
        getRestYPosition,
        updateAnchor,
    }
}
